import { useCallback } from 'react'
import {
    collection,
    doc,
    addDoc,
    getDoc,
    getDocs,
    updateDoc,
    Timestamp,
    serverTimestamp,
} from 'firebase/firestore'
import { firestore, auth } from '../config/firebase'
import { IGarage, Garage } from '../../../shared/types/garage.types'
import { COLLECTIONS } from '../../../shared/constants/collections'

type GarageUpdater = Partial<Omit<IGarage, 'createdAt' | 'createdBy' | 'updatedAt' | 'updatedBy'>>

interface IUseGarageReturn {
    get: (id: string) => Promise<Garage | null>
    list: () => Promise<Garage[]>
    create: (data: Omit<IGarage, 'createdAt' | 'createdBy' | 'updatedAt' | 'updatedBy'>) => Promise<Garage>
    edit: (id: string, data: GarageUpdater) => Promise<void>
}

const convertDoc = (uid: string, data: Record<string, unknown>): Garage => ({
    uid,
    ...data,
    createdAt: data.createdAt instanceof Timestamp
        ? data.createdAt.toDate()
        : new Date(data.createdAt as string),
} as Garage)

const useGarage = (): IUseGarageReturn => {

    /**
     * Načte jednu garáž podle jejího ID.
     * @param id - Firestore document ID garáže
     */
    const get = useCallback(async (id: string): Promise<Garage | null> => {
        const snapshot = await getDoc(doc(firestore, COLLECTIONS.GARAGE, id))
        if (!snapshot.exists()) return null
        return convertDoc(snapshot.id, snapshot.data())
    }, [])

    /**
     * Načte seznam všech garáží.
     */
    const list = useCallback(async (): Promise<Garage[]> => {
        const snapshot = await getDocs(collection(firestore, COLLECTIONS.GARAGE))
        return snapshot.docs.map((d) => convertDoc(d.id, d.data()))
    }, [])

    /**
     * Vytvoří novou garáž. Pole `createdAt` je nastaveno serverem.
     * @param data - Data garáže bez `createdAt`
     */
    const create = useCallback(async (data: Omit<IGarage, 'createdAt' | 'createdBy' | 'updatedAt' | 'updatedBy'>): Promise<Garage> => {
        const ref = await addDoc(collection(firestore, COLLECTIONS.GARAGE), {
            ...data,
            createdAt: serverTimestamp(),
            createdBy: auth.currentUser?.uid ?? null,
        })
        return { uid: ref.id, ...data, createdAt: new Date() }
    }, [])

    /**
     * Aktualizuje pole existující garáže.
     * @param id - Firestore document ID garáže
     * @param data - Pole k aktualizaci
     */
    const edit = useCallback(async (id: string, data: GarageUpdater): Promise<void> => {
        if (!auth.currentUser) throw new Error('User must be signed in to edit garage')
        await updateDoc(doc(firestore, COLLECTIONS.GARAGE, id), {
            ...data,
            updatedAt: serverTimestamp(),
            updatedBy: auth.currentUser!.uid,
        })
    }, [])

    return { get, list, create, edit }
}

export default useGarage
